import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaSearch, FaTimes, FaGraduationCap, FaCertificate, FaThLarge, 
  FaList, FaExternalLinkAlt, FaAward, FaUniversity, FaBrain, 
  FaLayerGroup, FaCheck, FaBookOpen
} from 'react-icons/fa';
import { courses } from '../data/courses';

const categoryColorMap = {
  'Theory': {
    badge: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20',
    dot: 'bg-blue-500',
    icon: <FaLayerGroup size={12} />
  },
  'AI/ML': {
    badge: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20',
    dot: 'bg-purple-500',
    icon: <FaBrain size={12} />
  },
  'Systems': {
    badge: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
    dot: 'bg-emerald-500',
    icon: <FaAward size={12} />
  },
  'Default': {
    badge: 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/20',
    dot: 'bg-zinc-500',
    icon: <FaBookOpen size={12} />
  }
};

const Courses = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('title:asc');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Close modal on escape
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape') setSelectedCourse(null);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(courses.map(c => c.category))];
    return ['All', ...cats];
  }, []);

  // Compute learning analytics
  const analytics = useMemo(() => {
    const totalCourses = courses.length;
    const verifiedCertificates = courses.filter(c => Boolean(c.certificateLink)).length;
    const totalCategories = new Set(courses.map(c => c.category)).size;
    const totalInstitutions = new Set(
      courses.map(c => c.institution || (c.description?.includes("San Diego") ? "UC San Diego" : c.description?.includes("DeepLearning") ? "DeepLearning.AI" : "Coursera"))
    ).size;

    return { totalCourses, verifiedCertificates, totalCategories, totalInstitutions };
  }, []);

  // Filter and sort courses
  const filteredAndSortedCourses = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let working = courses;

    if (selectedCategory !== 'All') {
      working = working.filter(c => c.category === selectedCategory);
    }

    if (q) {
      working = working.filter(c => 
        (c.title || '').toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q) ||
        (c.category || '').toLowerCase().includes(q) ||
        (c.institution || '').toLowerCase().includes(q)
      );
    }

    const [field, dir] = sortBy.split(':');
    return working.slice().sort((a, b) => {
      let result = 0;
      if (field === 'title') {
        result = a.title.localeCompare(b.title);
      } else if (field === 'category') {
        result = a.category.localeCompare(b.category);
      }
      return dir === 'desc' ? -result : result;
    });
  }, [searchQuery, selectedCategory, sortBy]);

  const categoryCounts = useMemo(() => {
    const counts = { All: courses.length };
    courses.forEach(c => {
      counts[c.category] = (counts[c.category] || 0) + 1;
    });
    return counts;
  }, []);

  return (
    <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 max-w-7xl mx-auto space-y-12 select-text">
      
      {/* ========================================================= */}
      {/* 1. HEADER & INTRO */}
      {/* ========================================================= */}
      <div className="space-y-4 text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary dark:text-dark-primary border border-primary/20 backdrop-blur-md">
          <FaGraduationCap className="w-3.5 h-3.5" />
          <span>Academic & Professional Specializations</span>
        </div>
        
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-zinc-900 dark:text-white">
          Courses & Certifications
        </h1>
        <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed">
          Continuous learning in advanced algorithms, machine learning architectures, and modern software engineering paradigms.
        </p>
      </div>

      {/* ========================================================= */}
      {/* 2. M3 LEARNING ANALYTICS SUMMARY */}
      {/* ========================================================= */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Metric 1: Verified Credentials */}
        <div className="p-5 rounded-3xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
            <FaCertificate size={20} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Verified Honors</div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-white">{analytics.verifiedCertificates} Certificates</div>
          </div>
        </div>

        {/* Metric 2: Focus Domains */}
        <div className="p-5 rounded-3xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-purple-500/10 text-purple-600 dark:text-purple-400">
            <FaBrain size={20} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Specializations</div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-white">{analytics.totalCategories} Domains</div>
          </div>
        </div>

        {/* Metric 3: Academic Providers */}
        <div className="p-5 rounded-3xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <FaUniversity size={20} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Institutions</div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-white">{analytics.totalInstitutions} Universiti{analytics.totalInstitutions !== 1 ? 'es' : 'y'}</div>
          </div>
        </div>

        {/* Metric 4: Total Completed */}
        <div className="p-5 rounded-3xl bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
            <FaAward size={20} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Total Courses</div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-white">{analytics.totalCourses} Completed</div>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* 3. M3 SEARCH & FILTER CONTROLS */}
      {/* ========================================================= */}
      <div className="space-y-6">
        {/* Search Bar */}
        <div className="relative flex items-center">
          <FaSearch className="absolute left-5 text-zinc-400 pointer-events-none" size={16} />
          <input
            type="text"
            placeholder="Search courses by title, topic, or university..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-12 py-4 rounded-full bg-white/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800/80 text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 dark:focus:ring-dark-primary/40 backdrop-blur-md shadow-sm text-sm sm:text-base transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-5 p-1 rounded-full text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
              aria-label="Clear search"
            >
              <FaTimes size={14} />
            </button>
          )}
        </div>

        {/* Filter Chips & View Switcher */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          
          {/* Category Chips */}
          <div className="flex items-center flex-wrap gap-2">
            {categories.map(cat => {
              const isSelected = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 border ${
                    isSelected
                      ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 border-zinc-900 dark:border-white shadow-sm'
                      : 'bg-white/60 dark:bg-zinc-900/60 text-zinc-600 dark:text-zinc-400 border-zinc-200/80 dark:border-zinc-800/80 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                  }`}
                >
                  <span>{cat}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                    isSelected ? 'bg-white/20 text-white dark:bg-zinc-900/20 dark:text-zinc-900' : 'bg-zinc-200/60 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                  }`}>
                    {categoryCounts[cat] || 0}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Sort Selector & Segmented View Toggle */}
          <div className="flex items-center gap-3">
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-xs sm:text-sm font-medium px-4 py-2 rounded-full bg-white/70 dark:bg-zinc-900/70 border border-zinc-200/80 dark:border-zinc-800/80 text-zinc-700 dark:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-primary/40 backdrop-blur-md cursor-pointer"
            >
              <option value="title:asc">Title (A → Z)</option>
              <option value="title:desc">Title (Z → A)</option>
              <option value="category:asc">Category (A → Z)</option>
            </select>

            {/* M3 Segmented View Toggle */}
            <div className="p-1 rounded-full bg-zinc-200/60 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 flex items-center">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-full transition-all ${
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-zinc-900 text-primary dark:text-dark-primary shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                }`}
                title="Grid View"
              >
                <FaThLarge size={14} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-full transition-all ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-zinc-900 text-primary dark:text-dark-primary shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                }`}
                title="List View"
              >
                <FaList size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* 4. COURSES DISPLAY (GRID VS LIST) */}
      {/* ========================================================= */}
      {filteredAndSortedCourses.length === 0 ? (
        <div className="text-center py-20 bg-white/40 dark:bg-zinc-900/40 rounded-3xl border border-zinc-200/60 dark:border-zinc-800/60">
          <p className="text-zinc-500 text-base">No courses found matching your criteria.</p>
          <button
            onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
            className="mt-4 text-xs font-semibold text-primary dark:text-dark-primary hover:underline"
          >
            Reset Filters
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        /* GRID VIEW */
        <motion.div 
          layout
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <AnimatePresence>
            {filteredAndSortedCourses.map((course, index) => {
              const catConfig = categoryColorMap[course.category] || categoryColorMap['Default'];
              return (
                <motion.div
                  key={`${course.title}-${index}`}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.2 }}
                  className="group rounded-3xl bg-white/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md p-6 sm:p-8 flex flex-col justify-between hover:shadow-xl hover:border-zinc-400 dark:hover:border-zinc-600 transition-all duration-300"
                >
                  <div className="space-y-5">
                    {/* Header: Icon & Category Badge */}
                    <div className="flex items-center justify-between">
                      <div className="w-14 h-14 rounded-2xl bg-zinc-100 dark:bg-zinc-800 border border-zinc-200/60 dark:border-zinc-700/60 flex items-center justify-center text-3xl shadow-inner group-hover:scale-110 transition-transform duration-200">
                        {course.icon || "🎓"}
                      </div>

                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${catConfig.badge}`}>
                          {catConfig.icon}
                          <span>{course.category}</span>
                        </span>
                        
                        {course.certificateLink && (
                          <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 px-2.5 py-0.5 rounded-full">
                            <FaCheck size={10} />
                            <span>Verified</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Title & Description */}
                    <div className="space-y-2">
                      <div>
                        <h3 className="text-xl font-bold text-zinc-900 dark:text-white group-hover:text-primary dark:group-hover:text-dark-primary transition-colors">
                          {course.title}
                        </h3>
                        {course.institution && (
                          <div className="flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400 font-medium mt-1">
                            <FaUniversity size={11} className="text-zinc-400" />
                            <span>{course.institution}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed pt-1">
                        {course.description}
                      </p>
                    </div>
                  </div>

                  {/* Actions & Links */}
                  <div className="pt-6 mt-6 border-t border-zinc-200/60 dark:border-zinc-800/60 flex items-center justify-between gap-3">
                    <button
                      onClick={() => setSelectedCourse(course)}
                      className="text-xs font-semibold text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
                    >
                      Course Overview
                    </button>

                    {course.certificateLink && (
                      <a
                        href={course.certificateLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-xl bg-primary hover:bg-primary/90 dark:bg-dark-primary dark:hover:bg-dark-primary/90 text-white shadow-sm hover:shadow transition-all duration-200"
                      >
                        <FaCertificate size={12} />
                        <span>View Certificate</span>
                        <FaExternalLinkAlt size={10} />
                      </a>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      ) : (
        /* LIST / TABLE VIEW */
        <motion.div layout className="space-y-3">
          <AnimatePresence>
            {filteredAndSortedCourses.map((course, index) => {
              const catConfig = categoryColorMap[course.category] || categoryColorMap['Default'];
              return (
                <motion.div
                  key={`${course.title}-${index}`}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="group rounded-2xl bg-white/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800/80 backdrop-blur-md p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 hover:shadow-lg hover:border-zinc-400 dark:hover:border-zinc-600 transition-all"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-2xl shrink-0 border border-zinc-200/60 dark:border-zinc-700/60">
                      {course.icon || "🎓"}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-base text-zinc-900 dark:text-white truncate group-hover:text-primary dark:group-hover:text-dark-primary transition-colors">
                        {course.title}
                      </h3>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 line-clamp-1">
                        {course.institution ? `${course.institution} • ` : ''}{course.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end shrink-0">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${catConfig.badge}`}>
                      {catConfig.icon}
                      <span>{course.category}</span>
                    </span>

                    {course.certificateLink && (
                      <a
                        href={course.certificateLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-1.5 rounded-xl bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 transition-colors"
                      >
                        <FaCertificate size={12} className="text-primary dark:text-dark-primary" />
                        <span>Certificate</span>
                        <FaExternalLinkAlt size={10} />
                      </a>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      {/* ========================================================= */}
      {/* 5. M3 DETAILED COURSE INSPECTION DIALOG */}
      {/* ========================================================= */}
      <AnimatePresence>
        {selectedCourse && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/40 backdrop-blur-md"
            onClick={(e) => { if (e.target === e.currentTarget) setSelectedCourse(null); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="relative w-full max-w-lg rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl p-6 sm:p-8 space-y-6"
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedCourse(null)}
                className="absolute top-5 right-5 p-2.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
                aria-label="Close dialog"
              >
                <FaTimes size={14} />
              </button>

              {/* Course Header */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-zinc-100 dark:bg-zinc-800 border border-zinc-200/60 dark:border-zinc-700/60 flex items-center justify-center text-4xl shrink-0">
                  {selectedCourse.icon || "🎓"}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-0.5 rounded-full border ${categoryColorMap[selectedCourse.category]?.badge || categoryColorMap['Default'].badge}`}>
                      {selectedCourse.category}
                    </span>
                    {selectedCourse.institution && (
                      <span className="text-xs text-zinc-500 dark:text-zinc-400 font-medium flex items-center gap-1">
                        <FaUniversity size={10} />
                        <span>{selectedCourse.institution}</span>
                      </span>
                    )}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-extrabold text-zinc-900 dark:text-white leading-tight">
                    {selectedCourse.title}
                  </h2>
                </div>
              </div>

              {/* Course Overview Details */}
              <div className="space-y-3 p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200/60 dark:border-zinc-700/60 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
                <div className="font-semibold text-xs uppercase tracking-wider text-zinc-400 font-mono">Curriculum Focus</div>
                <p>{selectedCourse.description}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => setSelectedCourse(null)}
                  className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 transition-colors"
                >
                  Close
                </button>
                {selectedCourse.certificateLink && (
                  <a
                    href={selectedCourse.certificateLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-semibold bg-primary hover:bg-primary/90 dark:bg-dark-primary dark:hover:bg-dark-primary/90 text-white shadow-md transition-all"
                  >
                    <FaCertificate size={12} />
                    <span>Verify Credential</span>
                    <FaExternalLinkAlt size={10} />
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Courses;
